import { useState, useEffect, useMemo } from "react";
import { http, resolveImage } from "@/lib/api";
import { Link } from "react-router-dom";
import { ShoppingBag, Package, Search, X } from "lucide-react";

const C = {
  bg: "#070707", bg2: "#11100E", card: "#0d0c0a", text: "#F5F2EB", text2: "#B5B0A7",
  gold: "#C9A055", purple: "#7C3AED",
};
const F = { cond: "'Saira Condensed', sans-serif", sans: "'DM Sans', 'Outfit', sans-serif", brush: "'Permanent Marker', cursive" };

export default function TheMarket() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeTags, setActiveTags] = useState([]);
  const [search, setSearch] = useState("");
  const [ps, setPs] = useState({
    subtitle: "BY THE MARKER COLLECTION",
    title: "THE MARKET",
    description: "Hats, books, prints, and more. Everything hand-picked, everything with a story.",
    badge_left: "Free Shipping $75+",
    badge_right: "In-Person & Online",
    cta_text: "LOOKING FOR ORIGINAL ART?",
    cta_button: "Visit The Marker Collection",
    cta_link: "/marker-collection",
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, cRes, tRes, psRes] = await Promise.all([
          http.get("/marker-collection/public/market/products"),
          http.get("/marker-collection/public/market/categories"),
          http.get("/marker-collection/public/market/tags"),
          http.get("/marker-collection/public/market/page-settings").catch(() => ({ data: {} })),
        ]);
        setProducts(pRes.data || []);
        setCategories(cRes.data || []);
        setTags(tRes.data || []);
        if (psRes.data) setPs(prev => ({ ...prev, ...psRes.data }));
      } catch {}
      finally { setLoading(false); }
    };
    load();
  }, []);

  const toggleTag = (tagName) => {
    setActiveTags(prev =>
      prev.includes(tagName) ? prev.filter(t => t !== tagName) : [...prev, tagName]
    );
  };

  // Filter logic: category → tags → search
  const filtered = useMemo(() => {
    let items = products;
    if (activeCategory !== "all") {
      items = items.filter(p => p.category_id === activeCategory);
    }
    if (activeTags.length > 0) {
      items = items.filter(p =>
        activeTags.some(t => (p.tags || []).includes(t))
      );
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(p =>
        (p.title || "").toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q) ||
        (p.category_name || "").toLowerCase().includes(q) ||
        (p.vendor_code || "").toLowerCase().includes(q) ||
        (p.tags || []).some(t => t.toLowerCase().includes(q))
      );
    }
    return items;
  }, [products, activeCategory, activeTags, search]);

  // Tags relevant to current category filter
  const visibleTags = useMemo(() => {
    const relevant = activeCategory === "all" ? products : products.filter(p => p.category_id === activeCategory);
    const tagCounts = {};
    relevant.forEach(p => (p.tags || []).forEach(t => { tagCounts[t] = (tagCounts[t] || 0) + 1; }));
    return tags.filter(t => tagCounts[t.name]).map(t => ({ ...t, count: tagCounts[t.name] }));
  }, [tags, products, activeCategory]);

  return (
    <div className="min-h-screen" style={{ background: C.bg, color: C.text }}>
      {/* ─── Hero ─── */}
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${C.bg} 0%, #1a1510 50%, ${C.bg} 100%)` }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, #C9A055 0%, transparent 50%), radial-gradient(circle at 80% 50%, #7C3AED 0%, transparent 50%)" }} />
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-28 relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px flex-1 max-w-[60px]" style={{ background: C.gold }} />
            <span className="text-[10px] tracking-[0.4em] uppercase" style={{ fontFamily: F.sans, color: C.gold }}>
              {ps.subtitle}
            </span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.9]"
            style={{ fontFamily: F.cond, color: C.text }} data-testid="market-hero-title">
            {ps.title}
          </h1>
          <p className="mt-4 text-base sm:text-lg max-w-xl leading-relaxed" style={{ fontFamily: F.sans, color: C.text2 }}>
            {ps.description}
          </p>
          <div className="mt-6 flex items-center gap-4 text-[10px] tracking-[0.2em] uppercase" style={{ fontFamily: F.sans, color: `${C.gold}80` }}>
            <span className="flex items-center gap-1.5"><Package size={14} /> {ps.badge_left}</span>
            <span className="w-1 h-1 rounded-full" style={{ background: C.gold }} />
            <span className="flex items-center gap-1.5"><ShoppingBag size={14} /> {ps.badge_right}</span>
          </div>
        </div>
      </section>

      {/* ─── Filter Bar: Categories + Search ─── */}
      <div className="sticky top-0 z-20" style={{ background: C.bg2, borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex items-center gap-3" data-testid="market-category-filters">
            <div className="flex gap-2 overflow-x-auto no-scrollbar flex-1">
              <button onClick={() => { setActiveCategory("all"); setActiveTags([]); }}
                className={`flex-shrink-0 px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-bold border transition-colors ${
                  activeCategory === "all"
                    ? "bg-[#C9A055] text-black border-[#C9A055]"
                    : "bg-transparent text-[#F5F2EB] border-white/10 hover:border-[#C9A055]/50"
                }`} style={{ fontFamily: F.sans }} data-testid="market-filter-all">
                All ({products.length})
              </button>
              {categories.map(cat => {
                const count = products.filter(p => p.category_id === cat.id).length;
                return (
                  <button key={cat.id} onClick={() => { setActiveCategory(cat.id); setActiveTags([]); }}
                    className={`flex-shrink-0 px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-bold border transition-colors ${
                      activeCategory === cat.id
                        ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                        : "bg-transparent text-[#F5F2EB] border-white/10 hover:border-[#7C3AED]/50"
                    }`} style={{ fontFamily: F.sans }} data-testid={`market-filter-${cat.code}`}>
                    {cat.name} ({count})
                  </button>
                );
              })}
            </div>
            {/* Search */}
            <div className="relative hidden sm:block w-48 flex-shrink-0">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6a6660]" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                className="w-full pl-8 pr-8 py-2 text-[11px] outline-none"
                style={{ fontFamily: F.sans, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: C.text }}
                placeholder="Search..." data-testid="market-search-input" />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6a6660] hover:text-white">
                  <X size={12} />
                </button>
              )}
            </div>
          </div>

          {/* Mobile Search */}
          <div className="sm:hidden mt-2 relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6a6660]" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-8 py-2.5 text-[11px] outline-none"
              style={{ fontFamily: F.sans, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: C.text }}
              placeholder="Search products, tags..." data-testid="market-search-mobile" />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6a6660] hover:text-white">
                <X size={12} />
              </button>
            )}
          </div>

          {/* Tag pills — shown when tags exist for this view */}
          {visibleTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2.5 pb-0.5" data-testid="market-tag-filters">
              {visibleTags.map(t => {
                const active = activeTags.includes(t.name);
                return (
                  <button key={t.id} onClick={() => toggleTag(t.name)}
                    className={`px-3 py-1.5 text-[9px] sm:text-[10px] tracking-[0.15em] uppercase font-medium border transition-all ${
                      active
                        ? "bg-purple-600/25 text-purple-200 border-purple-500/50"
                        : "bg-transparent text-[#8a857d] border-white/8 hover:border-purple-500/30 hover:text-purple-300"
                    }`} style={{ fontFamily: F.sans }} data-testid={`market-tag-${t.slug}`}>
                    {active && <span className="mr-1">✓</span>}{t.name}
                    <span className="ml-1 opacity-50">{t.count}</span>
                  </button>
                );
              })}
              {activeTags.length > 0 && (
                <button onClick={() => setActiveTags([])}
                  className="px-3 py-1.5 text-[9px] sm:text-[10px] tracking-[0.15em] uppercase text-red-400 border border-red-500/20 hover:bg-red-500/10 transition-colors"
                  style={{ fontFamily: F.sans }} data-testid="market-clear-tags">
                  <X size={10} className="inline mr-1" />Clear Tags
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ─── Product Grid ─── */}
      <div className="max-w-6xl mx-auto px-6 py-10">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-2 animate-spin" style={{ borderColor: `${C.gold}30`, borderTopColor: C.gold }} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingBag size={48} className="mx-auto mb-4" style={{ color: `${C.gold}20` }} />
            <p className="text-lg mb-2" style={{ fontFamily: F.cond, color: C.text2 }}>
              {products.length === 0 ? "Products coming soon!" : "No products match your filters."}
            </p>
            {(activeTags.length > 0 || search) && (
              <button onClick={() => { setActiveTags([]); setSearch(""); }}
                className="text-sm underline" style={{ color: C.gold }}>
                Clear all filters
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Result count */}
            {(activeTags.length > 0 || search) && (
              <p className="text-[10px] tracking-[0.2em] uppercase mb-4" style={{ fontFamily: F.sans, color: C.text2 }}>
                Showing {filtered.length} of {products.length} products
              </p>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="market-product-grid">
              {filtered.map(prod => {
                const outOfStock = prod.quantity != null && prod.quantity <= 0;
                return (
                  <Link key={prod.id} to={`/the-market/${prod.slug}`}
                    className="group block border transition-all hover:border-[#C9A055]/30"
                    style={{ background: C.card, borderColor: "rgba(255,255,255,0.06)" }}
                    data-testid={`market-product-${prod.id}`}>
                    <div className="relative aspect-square overflow-hidden">
                      {prod.main_image ? (
                        <img src={resolveImage(prod.main_image)} alt={prod.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center" style={{ background: C.bg2 }}>
                          <Package size={32} style={{ color: `${C.gold}15` }} />
                        </div>
                      )}
                      {/* Category badge */}
                      <div className="absolute top-2 left-2 px-2 py-0.5"
                        style={{ background: "rgba(124,58,237,0.9)", backdropFilter: "blur(4px)" }}>
                        <span className="text-[8px] font-bold tracking-[0.15em] uppercase text-white" style={{ fontFamily: F.sans }}>
                          {prod.category_name}
                        </span>
                      </div>
                      {/* Sale badge */}
                      {prod.on_sale && prod.sale_price > 0 && prod.sale_price < prod.price && (
                        <div className="absolute top-2 right-2 px-2 py-0.5"
                          style={{ background: "rgba(255,59,48,0.95)", backdropFilter: "blur(4px)" }}>
                          <span className="text-[8px] font-bold tracking-[0.15em] uppercase text-white" style={{ fontFamily: F.sans }}>
                            SALE
                          </span>
                        </div>
                      )}
                      {/* Price overlay */}
                      <div className="absolute bottom-2 right-2 px-2 py-1"
                        style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(4px)" }}>
                        {prod.on_sale && prod.sale_price > 0 && prod.sale_price < prod.price ? (
                          <span className="flex items-baseline gap-1.5">
                            <span className="text-base font-black" style={{ fontFamily: F.cond, color: "#FF3B30" }}>
                              ${Number(prod.sale_price).toFixed(0)}
                            </span>
                            <span className="text-xs font-bold line-through" style={{ fontFamily: F.cond, color: "#999" }}>
                              ${Number(prod.price || 0).toFixed(0)}
                            </span>
                          </span>
                        ) : (
                          <span className="text-base font-black" style={{ fontFamily: F.cond, color: C.gold }}>
                            ${Number(prod.price || 0).toFixed(0)}
                          </span>
                        )}
                      </div>
                      {outOfStock && (
                        <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.7)" }}>
                          <span className="text-sm tracking-[0.3em] uppercase font-black" style={{ fontFamily: F.cond, color: C.text }}>SOLD OUT</span>
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-bold truncate group-hover:text-[#C9A055] transition-colors"
                        style={{ fontFamily: F.sans, color: C.text }}>{prod.title}</h3>
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        {prod.vendor_code && (
                          <span className="text-[8px] font-mono" style={{ color: C.text2 }}>{prod.vendor_code}</span>
                        )}
                        {(prod.tags || []).slice(0, 2).map(t => (
                          <span key={t} className="text-[7px] tracking-wider uppercase px-1.5 py-0.5 bg-purple-900/20 text-purple-300 border border-purple-500/15">
                            {t}
                          </span>
                        ))}
                        {(prod.tags || []).length > 2 && (
                          <span className="text-[7px]" style={{ color: C.text2 }}>+{prod.tags.length - 2}</span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* ─── Footer CTA ─── */}
      <div className="border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="max-w-6xl mx-auto px-6 py-12 text-center">
          <p className="text-xs tracking-[0.3em] uppercase mb-2" style={{ fontFamily: F.sans, color: C.gold }}>
            {ps.cta_text}
          </p>
          <Link to={ps.cta_link}
            className="inline-block px-6 py-3 text-[11px] tracking-[0.2em] uppercase font-bold border transition-colors hover:bg-[#C9A055]/10"
            style={{ fontFamily: F.sans, color: C.gold, borderColor: `${C.gold}40` }}>
            {ps.cta_button}
          </Link>
        </div>
      </div>
    </div>
  );
}
